import { update as fbUpdate, set as fbSet, push as fbPush, remove as fbRemove } from "firebase/database";
import { firebase } from '../config/configureFirebase';
import { getNativeIdToken } from './getCurrentUid';

const getDbUrl = () => firebase.config?.databaseURL;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const extractPath = (ref) => {
  const fullUrl = ref.toString();
  const dbUrl = getDbUrl();
  if (dbUrl && fullUrl.startsWith(dbUrl)) {
    return fullUrl.slice(dbUrl.length);
  }
  return null;
};

const isJsSdkAuthed = () => !!firebase.auth?.currentUser;

const waitForNativeIdToken = async (timeoutMs = 5000, intervalMs = 250) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const token = await getNativeIdToken();
    if (token) {
      return token;
    }
    await delay(intervalMs);
  }

  return null;
};

const restApiRequest = async (path, method = 'GET', data = null) => {
  const idToken = await getNativeIdToken();
  if (!idToken) throw new Error('No native auth token available');
  const url = `${getDbUrl()}${path}.json?auth=${idToken}`;
  const options = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (data !== null) {
    options.body = JSON.stringify(data);
  }
  const response = await fetch(url, options);
  if (!response.ok) {
    const err = await response.text();
    throw new Error(`REST DB ${method} failed: ${response.status} ${err}`);
  }
  return response.json();
};

export const safeUpdate = async (ref, data) => {
  if (isJsSdkAuthed()) {
    return fbUpdate(ref, data);
  }
  const path = extractPath(ref);
  if (path) {
    return restApiRequest(path, 'PATCH', data);
  }
  return fbUpdate(ref, data);
};

export const safeSet = async (ref, data) => {
  if (isJsSdkAuthed()) {
    return fbSet(ref, data);
  }
  const path = extractPath(ref);
  if (path) {
    return restApiRequest(path, 'PUT', data);
  }
  return fbSet(ref, data);
};

export const safePush = async (ref, data) => {
  if (isJsSdkAuthed()) {
    return fbPush(ref, data);
  }
  const path = extractPath(ref);
  if (path) {
    return restApiRequest(path, 'POST', data);
  }
  return fbPush(ref, data);
};

export const safeRemove = async (ref) => {
  if (isJsSdkAuthed()) {
    return fbRemove(ref);
  }
  const path = extractPath(ref);
  if (path) {
    return restApiRequest(path, 'DELETE');
  }
  return fbRemove(ref);
};

export const safeRead = async (path, forceRest = false) => {
  if (isJsSdkAuthed() && !forceRest) return null;
  return restApiRequest(path, 'GET');
};

export const safeQueryRead = async (path, orderBy, equalTo, forceRest = false) => {
  if (isJsSdkAuthed() && !forceRest) return null;
  const idToken = await waitForNativeIdToken();
  if (!idToken) {
    throw new Error('No native auth token available');
  }
  const url = `${getDbUrl()}${path}.json?auth=${idToken}&orderBy="${orderBy}"&equalTo="${equalTo}"`;
  const response = await fetch(url);
  if (!response.ok) {
    const err = await response.text();
    throw new Error(`REST DB query failed: ${response.status} ${err}`);
  }
  return response.json();
};
